import type { OrgChartDoc, Selection } from '../types';

function describeSelection(doc: OrgChartDoc, selection: Selection): { label: string | undefined; kind: string } | null {
  if (!selection) return null;
  if (selection.kind === 'sun') return { label: 'Program leadership', kind: 'Leadership' };
  const group = doc.groups[selection.gi];
  if (!group) return null;
  if (selection.kind === 'group') return { label: group.short, kind: 'Group' };
  const program = group.programs[selection.pi];
  if (!program) return null;
  if (selection.kind === 'prog') return { label: program.short, kind: 'Program' };
  const person = program.people[selection.ki];
  if (!person) return null;
  return { label: person.vacant ? `Vacant · ${person.role}` : person.name, kind: 'Employee' };
}

function KeyHints({ selection, described }: { selection: Selection; described: { label: string | undefined; kind: string } | null }) {
  if (!described) return <>Click any dot, program disc or group disc to select it.</>;
  const sel = selection as Exclude<Selection, null>;
  if (sel.kind === 'person') {
    return (
      <>
        <kbd>←</kbd><kbd>→</kbd> slide on orbit · <kbd>R</kbd> label style · <kbd>Del</kbd> remove
      </>
    );
  }
  if (sel.kind === 'sun') {
    return (
      <>
        <kbd>drag</kbd> move · <kbd>-</kbd><kbd>=</kbd> resize
      </>
    );
  }
  return (
    <>
      <kbd>drag</kbd> move · <kbd>←↑→↓</kbd> nudge · <kbd>-</kbd><kbd>=</kbd> disc size · <kbd>[</kbd><kbd>]</kbd> orbit size · <kbd>Del</kbd> remove
    </>
  );
}

interface SelectionStatusBarProps {
  doc: OrgChartDoc;
  selection: Selection;
  editMode: boolean;
}

export default function SelectionStatusBar({ doc, selection, editMode }: SelectionStatusBarProps) {
  if (!editMode) return null;

  const described = describeSelection(doc, selection);

  return (
    <div className="tbrow">
      <span className={`selchip${described ? '' : ' none'}`}>
        {described ? `${described.kind} · ${described.label}` : 'Nothing selected'}
      </span>
      <span className="keys"><KeyHints selection={selection} described={described} /></span>
    </div>
  );
}

interface EditNoteProps {
  editMode: boolean;
}

export function EditNote({ editMode }: EditNoteProps) {
  if (!editMode) return null;
  return (
    <p className="editnote">
      <b>Double-click anything</b> — person, program, group or the centre — to open its editor
      {' '}· <b>drag</b> employees along an orbit or onto another ring
      {' '}· <b>drag</b> discs to reposition (their people follow)
      {' '}· <b>drag the handles</b> to resize · hold <b>Shift</b> to snap.
    </p>
  );
}
